import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button as Button95, Divider, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { Link, RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
});

export default function ResetPassword() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const passwordInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { token } = useLocalSearchParams<{ token: string }>();
  const dispatch = useDispatch();
  const [error, setError] = useState("");

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      password: '',
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();
      setError("");

      const response = await AppUtilities.fetchUnauthorized(
        "ResetPassword.onSubmit",
        "/rabbl/api/resetpassword",
        "POST",
        {
          token: token,
          password: data.password,
        }
      );

      AppUtils.showInformationalToast("Password successfully changed. Try logging in.");
      router.replace("/signin");

    } catch (e : any) {
      AppUtilities.handleException(e, "ResetPassword.onSubmit");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      setError(AppUtilities.getErrors(e));
    } finally {
      hideSpinner();
    }
  }, [communityMembership]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (passwordInput && passwordInput.current) {
        passwordInput.current?.focus();
      }
    }
  }, [passwordInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Reset password"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <AppText>Enter your new password:</AppText>

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                ref={passwordInput}
                value={value}
                onChangeText={onChange}
                placeholder="Password"
                style={styles.formElement}
                returnKeyType="go"
                onBlur={onBlur}
                autoComplete="password"
                textContentType="password"
                onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                secureTextEntry={true}
              />
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Password is required. ^</AppText>}

          <View style={styles.inputWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Reset password</Button95>
          </View>

          { (error && error.length > 0) ?
                (
                  <>
                    <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>{error}</AppText>

                    {
                      error.indexOf("The password change token expired or could not be found") != -1 &&
                        <Link href="/forgotpassword" asChild className={"nontext"}><Button95>Request new reset</Button95></Link>
                    }
                  </>
                )
              :
                null
          }
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
